import type { ApiEducationStatistics } from '@mfw/common';

import type {
  UpdateEpisodeMarketplaceConfigArgs,
  FetchEpisodeSignedUploadUrlArgs,
  UpsertEpisodeProgressArgs,
  FetchEpisodesProgressArgs,
  UnarchiveEpisodeByIdArgs,
  FetchEpisodeProgressArgs,
  ArchiveEpisodeByIdArgs,
  FetchEpisodeByIdArgs,
  FetchEpisodesArgs,
  CreateEpisodeArgs,
  EditEpisodeArgs,
} from './types/episodes.types';
import type {
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type { EpisodeProgress, ApiEpisode } from '../schemas/episodes.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createEpisodeApiArgs,
  mapEpisodeFromApi,
} from './mappers/episodes.mappers';
import { postWithPagination, destroy, patch, post, put, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

const base = (organizationId?: string) =>
  `/v2${organizationId ? `/organizations/${organizationId}` : ''}/education/episodes`;

export const fetchEpisodeSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchEpisodeSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `${base(organizationId)}/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const createEpisode = async ({
  organizationId,
  ...args
}: CreateEpisodeArgs) => {
  const response = await post<ApiEpisode>(
    base(organizationId),
    createEpisodeApiArgs(args),
  );
  if (!response?.data) return null;
  return mapEpisodeFromApi(response.data, args.locale);
};

export const editEpisodeById = async ({
  id,
  organizationId,
  ...args
}: EditEpisodeArgs) => {
  const response = await patch<ApiEpisode>(
    `${base(organizationId)}/${id}`,
    createEpisodeApiArgs(args),
  );
  if (!response?.data) return null;
  return mapEpisodeFromApi(response.data, args.locale);
};

export const fetchEpisodeById = async ({
  id,
  locale,
  organizationId,
}: FetchEpisodeByIdArgs) => {
  const response = await get<BaseApiResponse<ApiEpisode>>(
    `${base(organizationId)}/${id}`,
  );
  if (!response?.data) return null;
  return mapEpisodeFromApi(response.data, locale);
};

export const fetchEpisodeStatistics = async ({
  id,
  organizationId,
}: FetchEpisodeByIdArgs) => {
  const response = await get<BaseApiResponse<ApiEducationStatistics>>(
    `${base(organizationId)}/${id}/statistics`,
  );
  return response?.data ?? null;
};

export const fetchEpisodes = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchEpisodesArgs) => {
  const { search, status, ...restTableFilters } = tableFilters;
  const { authorIds, isHome, isMarketplace, sources, tags } =
    contentFilters || {};

  // active => isDeleted:false, archived => isDeleted:true, all => omit (both).
  const isDeleted =
    status === 'archived' ? true : status === 'all' ? undefined : false;

  const response = await postWithPagination<ApiEpisode[]>(
    `${base(organizationId)}/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      // no source keeps the organization's own view; the backend narrows to
      // exactly the sources picked (the platform library is one of them)
      ...(sources?.length ? { sources } : {}),
      ...(isDeleted !== undefined ? { isDeleted } : {}),
      authorIds,
      isHome,
      isMarketplace,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response.data.map((episode) => mapEpisodeFromApi(episode, locale)),
  };
};

export const archiveEpisodeById = async ({
  id,
  organizationId,
}: ArchiveEpisodeByIdArgs) => {
  await destroy(`${base(organizationId)}/${id}`);
  return true;
};

export const unarchiveEpisodeById = async ({
  id,
  organizationId,
}: UnarchiveEpisodeByIdArgs) => {
  await patch(`${base(organizationId)}/${id}/restore`);
  return true;
};

export const fetchEpisodeProgress = async ({
  id,
  courseId,
  moduleId,
  organizationId,
}: FetchEpisodeProgressArgs) => {
  const query = new URLSearchParams();
  if (moduleId) query.set('moduleId', moduleId);
  if (courseId) query.set('courseId', courseId);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await get<BaseApiResponse<EpisodeProgress>>(
    `${base(organizationId)}/${id}/progress${suffix}`,
  );
  return response?.data ?? null;
};

export const fetchEpisodesProgress = async ({
  items,
  organizationId,
}: FetchEpisodesProgressArgs) => {
  if (!items.length) return [];
  const response = await post<EpisodeProgress[]>(
    `${base(organizationId)}/progress/list`,
    { items },
  );
  return response?.data ?? [];
};

export const upsertEpisodeProgress = async ({
  id,
  courseId,
  moduleId,
  organizationId,
  status,
}: UpsertEpisodeProgressArgs) => {
  const response = await put<EpisodeProgress>(
    `${base(organizationId)}/${id}/progress`,
    {
      status,
      ...(moduleId ? { moduleId } : {}),
      ...(moduleId && courseId ? { courseId } : {}),
    },
  );
  return response?.data ?? null;
};

export const updateEpisodeMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateEpisodeMarketplaceConfigArgs) => {
  await patch(`${base(organizationId)}/${id}/marketplace-config`, {
    configuration: {
      marketplace: { currency, enabled, price: enabled ? price : 0 },
    },
  });
};
